import React, { useEffect, useRef } from 'react';

const defaultNodes = [
  { id: 'payments-db', label: 'payments-db', type: 'database', x: 400, y: 50 },
  { id: 'checkout-service', label: 'checkout-service', type: 'service', x: 200, y: 150 },
  { id: 'order-service', label: 'order-service', type: 'service', x: 400, y: 150 },
  { id: 'refund-service', label: 'refund-service', type: 'service', x: 600, y: 150 },
  { id: 'frontend-web', label: 'frontend-web', type: 'app', x: 100, y: 280 },
  { id: 'mobile-api', label: 'mobile-api', type: 'app', x: 300, y: 280 },
  { id: 'support-portal', label: 'support-portal', type: 'app', x: 500, y: 280 },
  { id: 'finance-api', label: 'finance-api', type: 'app', x: 700, y: 280 },
  { id: 'customers', label: 'customers', type: 'user', x: 200, y: 380 },
  { id: 'warehouse', label: 'warehouse', type: 'user', x: 400, y: 380 }
];

const defaultEdges = [
  { from: 'payments-db', to: 'checkout-service' },
  { from: 'payments-db', to: 'order-service' },
  { from: 'payments-db', to: 'refund-service' },
  { from: 'checkout-service', to: 'frontend-web' },
  { from: 'checkout-service', to: 'mobile-api' },
  { from: 'order-service', to: 'mobile-api' },
  { from: 'refund-service', to: 'support-portal' },
  { from: 'refund-service', to: 'finance-api' },
  { from: 'frontend-web', to: 'customers' },
  { from: 'mobile-api', to: 'customers' },
  { from: 'order-service', to: 'warehouse' }
];

const typeColors = {
  database: { fill: '#4a70ff', stroke: '#3a5fe6' },
  service: { fill: '#10b981', stroke: '#059669' },
  app: { fill: '#f59e0b', stroke: '#d97706' },
  user: { fill: '#8b5cf6', stroke: '#7c3aed' },
  infra: { fill: '#6b7280', stroke: '#4b5563' }
};

export const GraphView = ({ nodes = defaultNodes, edges = defaultEdges, width = 800, height = 450, className = '', onNodeClick }) => {
  const svgRef = useRef(null);

  const getNodeById = (id) => nodes.find(n => n.id === id);

  const handleNodeClick = (nodeId) => {
    if (onNodeClick) {
      onNodeClick(nodeId);
    }
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${className}`}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        style={{ minHeight: '300px' }}
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#9ca3af" />
          </marker>
        </defs>

        {/* Edges */}
        {edges.map((edge, idx) => {
          const fromNode = getNodeById(edge.from);
          const toNode = getNodeById(edge.to);
          if (!fromNode || !toNode) return null;
          
          return (
            <g key={idx}>
              <line
                x1={fromNode.x}
                y1={fromNode.y + 20}
                x2={toNode.x}
                y2={toNode.y - 20}
                stroke="#d1d5db"
                strokeWidth="2"
                markerEnd="url(#arrowhead)"
                className="transition-all duration-300"
              />
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map((node) => {
          const colors = typeColors[node.type] || typeColors.service;
          return (
            <g 
              key={node.id} 
              className="cursor-pointer"
              onClick={() => handleNodeClick(node.id)}
            >
              <circle
                cx={node.x}
                cy={node.y}
                r="24"
                fill={colors.fill}
                stroke={colors.stroke}
                strokeWidth="3"
                className="transition-all duration-300 hover:filter hover:brightness-110"
              />
              <text
                x={node.x}
                y={node.y + 45}
                textAnchor="middle"
                className="text-xs font-medium fill-gray-700 pointer-events-none"
              >
                {node.label}
              </text>
            </g>
          );
        })}
      </svg>
      
      {/* Legend */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex flex-wrap gap-4">
        {Object.entries(typeColors).map(([type, colors]) => (
          <div key={type} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: colors.fill }}
            />
            <span className="text-xs text-gray-600 capitalize">{type}</span>
          </div>
        ))}
      </div>
    </div>
  );
};